import React from "react";
import styled from "styled-components";
import NsaLogo from "../pngs/nsabrandslogo.png";
import { typography, spacing, media } from "../styles/designSystem";
import { PageHeadline, SectionHeadline, TerminalLabel } from "../components/Typography";
import InfoCard from "../components/sub-components/InfoCard";
import TestimonialSection from "../components/TestimonialsSection";
import NDADisclosure from "../components/NDADisclosure";
import {
    RentalStateFlow,
    VisionPipeline,
    MegamindFlow,
    BuildDomains,
} from "../components/NsaDiagrams";

/**
 * NSA Brands.
 *
 * The through-line is software that operates a physical process rather than
 * documenting one: a rental state machine that ends by unlocking a door, and a
 * vision pipeline whose most important property is knowing when to refuse to answer.
 */

const PageWrapper = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    padding: ${spacing[900]} ${spacing[700]} ${spacing[1100]};
    color: ${({ theme }) => theme.text};

    ${media.md} {
        padding: ${spacing[700]} ${spacing[400]} ${spacing[900]};
    }
`;

const Header = styled.div`
    border: 1px solid ${({ theme }) => theme.borderMedium};
    background: ${({ theme }) => theme.cardBackground};
    box-shadow: inset 0 0 50px rgba(0, 0, 0, 0.55);
    padding: ${spacing[800]};
    margin-bottom: ${spacing[900]};

    ${media.md} {
        padding: ${spacing[600]};
    }
`;

const Logo = styled.img`
    display: block;
    max-width: 220px;
    height: auto;
    filter: ${({ theme }) => theme.logoFilter};
    margin-bottom: ${spacing[700]};

    ${media.md} {
        max-width: 160px;
    }
`;

const Meta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing[300]} ${spacing[700]};
    margin-bottom: ${spacing[600]};
`;

const MetaItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing[100]};

    span:last-child {
        font-size: ${typography.fontSize[200]};
        color: ${({ theme }) => theme.text};
    }
`;

const Lede = styled.p`
    font-size: ${typography.fontSize[400]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.text};
    margin: 0;
    max-width: 62ch;
`;

const Layout = styled.div`
    display: grid;
    grid-template-columns: 280px minmax(0, 1fr);
    gap: ${spacing[900]};
    align-items: start;

    ${media.lg} {
        grid-template-columns: 1fr;
        gap: ${spacing[700]};
    }
`;

const Sidebar = styled.aside`
    position: sticky;
    top: calc(var(--chrome-h, 145px) + ${spacing[600]});
    align-self: start;

    ${media.lg} {
        position: static;
    }
`;

const Body = styled.div`
    min-width: 0;
`;

const Section = styled.section`
    margin-bottom: ${spacing[1000]};
`;

const Para = styled.p`
    font-size: ${typography.fontSize[300]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    margin: 0 0 ${spacing[500]};
    max-width: 68ch;

    strong {
        color: ${({ theme }) => theme.text};
        font-weight: ${typography.fontWeight.semibold};
    }
`;

const Bullets = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0 0 ${spacing[600]};
    max-width: 68ch;
    columns: 2;
    column-gap: ${spacing[700]};

    ${media.md} {
        columns: 1;
    }
`;

const Bullet = styled.li`
    font-size: ${typography.fontSize[200]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    padding-left: ${spacing[500]};
    position: relative;
    margin-bottom: ${spacing[200]};
    break-inside: avoid;

    &::before {
        content: "\\25B8";
        position: absolute;
        left: 0;
        color: ${({ theme }) => theme.primary};
    }
`;

// The framing question, and the principle that came out of the AI work.
const Pull = styled.blockquote`
    border: 1px solid ${({ theme }) => theme.primary};
    border-left-width: 2px;
    background: ${({ theme }) => theme.glowSoft};
    padding: ${spacing[600]};
    margin: ${spacing[700]} 0;
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[300]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ theme }) => theme.primary};
    line-height: ${typography.lineHeight[500]};
`;

const DiagramSlot = styled.div`
    margin: ${spacing[700]} 0;
`;

const NsaBrands = () => (
    <PageWrapper>
        <Header>
            <Logo src={NsaLogo} alt="NSA Brands logo" />
            <PageHeadline>NSA Brands</PageHeadline>
            <Meta>
                <MetaItem>
                    <TerminalLabel>Role</TerminalLabel>
                    <span>Senior Software Engineer &mdash; AI &amp; Mobile</span>
                </MetaItem>
                <MetaItem>
                    <TerminalLabel>Focus</TerminalLabel>
                    <span>React Native &middot; Full-Stack &middot; AI/Vision &middot; Analytics</span>
                </MetaItem>
                <MetaItem>
                    <TerminalLabel>Service Period</TerminalLabel>
                    <span>Jun 2024 &mdash; Jun 2026</span>
                </MetaItem>
            </Meta>
            <Lede>
                NSA operates self-storage facilities where most of the critical moments happen with
                no employee present. Renting a unit, entering a property, managing an account, moving
                out, verifying a unit is empty &mdash; each one is the same engineering problem:{" "}
                <strong>how do you turn employee-driven operations into reliable self-service
                software?</strong>
            </Lede>
        </Header>

        <Layout>
            <Sidebar>
                <InfoCard
                    dates="Jun 2024 – Jun 2026"
                    roles={["Senior Software Engineer", "Mobile Engineering", "AI & Computer Vision"]}
                    tools={["React Native", "Node.js", "Computer Vision", "Multimodal AI", "REST APIs"]}
                />
            </Sidebar>

            <Body>
                <Section>
                    <SectionHeadline>The Engineering Problem</SectionHeadline>
                    <Para>
                        Self-storage looks simple from the outside. The underlying workflow is not. A
                        customer discovers a property, selects a unit, completes a rental, signs
                        documents, pays, receives access credentials, enters a facility, manages an
                        account, and eventually moves out. Traditionally an employee absorbs every
                        exception between those steps.
                    </Para>
                    <Para>Automating that meant engineering for:</Para>
                    <Bullets>
                        <Bullet>Authentication and account state</Bullet>
                        <Bullet>Unit availability</Bullet>
                        <Bullet>Rental workflows</Bullet>
                        <Bullet>Payments</Bullet>
                        <Bullet>Facility access</Bullet>
                        <Bullet>QR-driven entry points</Bullet>
                        <Bullet>Mobile devices</Bullet>
                        <Bullet>Network failures</Bullet>
                        <Bullet>Incomplete customer information</Bullet>
                        <Bullet>Physical property conditions</Bullet>
                        <Bullet>Operational exceptions</Bullet>
                    </Bullets>
                    <Para>
                        The goal was not to digitize an existing process. It was to build software
                        capable of <strong>operating</strong> it.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>Touch-Free Rentals</SectionHeadline>
                    <Para>
                        Customers could enter through web, mobile, kiosk, or a QR code on the
                        property. The engineering challenge was holding one consistent transaction
                        together as a person moved between those systems.
                    </Para>
                    <Para>
                        So the rental was modelled as a stateful workflow rather than a sequence of
                        screens. Failures got explicit recovery paths instead of being dumped back at
                        the start &mdash; which matters a great deal more when the software is
                        controlling access to something in the physical world.
                    </Para>
                    <DiagramSlot>
                        <RentalStateFlow />
                    </DiagramSlot>
                </Section>

                <Section>
                    <SectionHeadline>Mobile Product Engineering</SectionHeadline>
                    <Para>
                        The mobile app had to support customers across the whole storage lifecycle,
                        not act as a phone-shaped version of the website. That meant persistent
                        sessions, account and rental state, payments, facility information, access
                        workflows, notifications, device permissions, API failures, offline and
                        loading conditions, and interaction patterns native to the platform. It
                        shipped through the production app-store release process.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>Engineering AI Into Operations</SectionHeadline>
                    <Para>
                        When a customer leaves a unit, someone normally has to decide whether it is
                        actually empty and ready to rent again. That sounds like a simple computer
                        vision problem: take a picture, ask a model if the unit is empty.
                    </Para>
                    <Pull>AI confidence is not operational certainty.</Pull>
                    <Para>
                        A model can misread shadows, poor lighting, camera angles, partially visible
                        items, or a low-quality image. So the system could not ask for a yes/no and
                        trust the answer. I built it as a layered validation system instead, where
                        the model is <strong>one signal inside a larger decision system</strong>{" "}
                        rather than the system itself. Responses were constrained into structured
                        outputs that application logic could validate before deciding what happened
                        next.
                    </Para>
                    <DiagramSlot>
                        <VisionPipeline />
                    </DiagramSlot>
                    <Para>
                        When the system could not confidently determine the state of a unit, the
                        correct behavior was not to guess &mdash; it was to escalate for human
                        review. That boundary became one of the most important parts of the
                        architecture.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>Preventing Bad Inputs</SectionHeadline>
                    <Para>
                        There was a second problem. If users could upload arbitrary images, they
                        could submit one &mdash; accidentally or otherwise &mdash; that did not
                        represent the current condition of the unit. So the workflow requires a live
                        in-app camera capture rather than accepting anything from the device library.
                    </Para>
                    <Pull>
                        Don&rsquo;t rely on the model to solve problems you can prevent through
                        product architecture.
                    </Pull>
                    <Para>
                        Constraining the input cut the number of situations the AI system had to
                        reason about at all. That is usually cheaper and more reliable than making
                        the model smarter.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>Megamind</SectionHeadline>
                    <Para>
                        Storage operations generate data across occupancy, revenue, unit
                        availability, pricing, competitor pricing, and property performance. Read
                        independently, each metric tells part of the story. Megamind brought them
                        together so operators could see not only what was happening but what they
                        could do about it.
                    </Para>
                    <Para>
                        Rather than another collection of dashboards, we structured it around
                        operational questions. Which facilities are underperforming? Where is
                        occupancy moving? How does our pricing compare with nearby competitors? Where
                        might pricing be leaving revenue on the table?
                    </Para>
                    <DiagramSlot>
                        <MegamindFlow />
                    </DiagramSlot>
                </Section>

                <Section>
                    <SectionHeadline>Physical and Digital Systems</SectionHeadline>
                    <Para>
                        NSA reinforced something central to how I build now: software rarely operates
                        in isolation. A rental application decides whether someone can enter a
                        physical property. A vision decision decides whether a unit returns to
                        inventory. A pricing recommendation moves revenue across a facility.
                    </Para>
                    <Para>
                        Those connections change how systems have to be engineered. Failures need
                        recovery paths. AI needs boundaries. Physical workflows need validation. And
                        automated decisions need a clear point where the system knows{" "}
                        <strong>not</strong> to make a decision.
                    </Para>
                    <DiagramSlot>
                        <BuildDomains />
                    </DiagramSlot>
                </Section>

                <Section>
                    <SectionHeadline>Impact</SectionHeadline>
                    <Para>
                        The work moved traditionally employee-dependent storage operations toward
                        self-service across web, mobile, kiosk, and the properties themselves. More
                        to the point, it required building products where{" "}
                        <strong>frontend systems, backend data, AI models, business rules, and
                        physical operations all had to work together reliably</strong> &mdash; and
                        where the cost of getting it wrong showed up as a door that would not open.
                    </Para>
                </Section>
            </Body>
        </Layout>

        <TestimonialSection
            title="What a Colleague Said"
            testimonials={[
                {
                    name: "Johnny Keupper",
                    title: "Software Engineer III",
                    body: "Nicholas played a critical role in bringing our mobile app vision to life at NSA Brands. His front-end development skills—especially in building intuitive, responsive interfaces—elevated the overall user experience for our nationwide network of storage brands. He worked seamlessly with both product and engineering teams to implement features that directly improved customer engagement and streamlined the rental process. Nicholas consistently delivered clean, scalable code while keeping accessibility and performance top of mind. His ability to turn complex requirements into polished, production-ready features made him a cornerstone of the project.",
                },
            ]}
        />

        <NDADisclosure />
    </PageWrapper>
);

export default NsaBrands;
